import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { FaSmile, FaMeh, FaFrown, FaSadTear, FaChevronLeft, FaChevronRight, FaEllipsisH } from 'react-icons/fa';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFilter } from '../FilterContext';

function MainGrid({ data, currentPage, totalPages, totalRecs, onPageChange, onSortChange, sortModel, referrer }) {
  const { filters } = useFilter();
  const navigate = useNavigate();
  const location = useLocation();
  const gridApiRef = useRef(null);
  const currentSortModelRef = useRef(sortModel);
  const isUpdatingRef = useRef(false);
  const getSentimentIcon = (sentiment) => {
    const sentimentValue = parseFloat(sentiment);
    
    if (isNaN(sentimentValue)) {
      return null;
    }

    let icon;
    let color;

    if (sentimentValue > 0.75) {
      icon = <FaSmile size={24} />;
      color = 'text-green-400';
    } else if (sentimentValue > 0.25) {
      icon = <FaSmile size={24} />;
      color = 'text-green-200';
    } else if (sentimentValue >= -0.25) {
      icon = <FaMeh size={24} />;
      color = 'text-gray-300';
    } else if (sentimentValue >= -0.75) {
      icon = <FaFrown size={24} />;
      color = 'text-red-300';
    } else {
      icon = <FaSadTear size={24} />;
      color = 'text-red-500';
    }

    return (
      <Tippy content={`Sentiment: ${sentimentValue.toFixed(2)}`}>
        <span className={color}>{icon}</span>
      </Tippy>
    );
  };

  const getCustomerSatisfaction = (score, tipText) => {
    let text, color;
    if (score >= 0.8) {
      text = 'VERY GOOD';
      color = 'text-green-600';
    } else if (score >= 0.6) {
      text = 'GOOD';
      color = 'text-green-400';
    } else if (score >= 0.4) {
      text = 'NEUTRAL';
      color = 'text-gray-400';
    } else if (score >= 0.2) {
      text = 'BAD';
      color = 'text-red-400';
    } else {
      text = 'VERY BAD';
      color = 'font-semibold text-red-700';
    }
    return <Tippy content={tipText}><span className={color}>{text}</span></Tippy>;
  };

  const formatDate = (dateStr) => {
    return moment(dateStr).format("MM/DD/YYYY hh:mm:ss A");
  };

  const columnDefs = [
    {
      headerName: 'Contact ID',
      field: 'contact_id',
      cellRenderer: (params) => {
        const handleContextMenu = (event) => {
          event.preventDefault();
          
          // Remove any existing menu to prevent multiple menus
          const existingMenu = document.getElementById('custom-menu');
          if (existingMenu) {
            document.body.removeChild(existingMenu);
          }
    
          // Create the menu
          const menu = document.createElement('div');
          menu.id = 'custom-menu'; // Add an ID to easily reference it later
          menu.style.position = 'absolute';
          menu.style.left = `${event.pageX}px`;
          menu.style.top = `${event.pageY}px`;
          menu.style.border = '1px solid #ccc';
          menu.style.padding = '8px';
          menu.style.zIndex = 1000;
    
          // Create the "Open in New Window" option
          const openInNewWindowOption = document.createElement('div');
          openInNewWindowOption.textContent = 'Open in New Window';
          openInNewWindowOption.style.cursor = 'pointer';
          openInNewWindowOption.onclick = () => {
            window.open(`/contact/${params.value}`, '_blank');
            if (menu.parentNode) {
              menu.parentNode.removeChild(menu);
            }
          };
    
          menu.appendChild(openInNewWindowOption);
          document.body.appendChild(menu);
    
          // Remove the menu when clicking outside
          const handleClickOutside = (event) => {
            if (menu && !menu.contains(event.target)) {
              if (menu.parentNode) {
                menu.parentNode.removeChild(menu);
              }
              document.removeEventListener('click', handleClickOutside);
            }
          };
    
          document.addEventListener('click', handleClickOutside);
        };
    
        return (
          <span
            className="text-indigo-600 hover:text-indigo-800 cursor-pointer"
            onContextMenu={handleContextMenu}
            onClick={() => {
              navigate(`/contact/${params.value}`, { state: { filters: filters, referrer: location.pathname } });
            }}
          >
            {params.value}
          </span>
        );
      },
      width: 120,
    },
    {
      headerName: 'Date',
      field: 'call_date',
      valueFormatter: (params) => formatDate(params.value),
      width: 190
    },
    {
      headerName: 'Agent Name',
      headerTooltip: "Agent Name",
      field: 'agent',
      width: 140,
      cellRenderer: (params) => (
        <Tippy content={params.value}>
          <span>{params.value}</span>
        </Tippy>
      )
    },
    {
      headerName: 'Agent Sentiment',
      field: 'agent_sentiment',
      headerTooltip: "Agent Sentiment",
      cellRenderer: (params) => (
        <div className="flex justify-center items-center w-full h-full">
          {getSentimentIcon(params.value)}
        </div>
      ),
      width: 90,
    },
    {
      headerName: 'Customer Sentiment',
      headerTooltip: "Customer Sentiment",
      field: 'customer_sentiment',
      cellRenderer: (params) => (
        <div className="flex justify-center items-center w-full h-full">
          {getSentimentIcon(params.value)}
        </div>
      ),
      width: 90,
    },
    {
      headerName: 'Agent Performance',
      headerTooltip: "Agent Performance | How well did the agent perform his job?",
      field: 'agent_performance_score',
      cellRenderer: (params) => (
        <Tippy content={params.data.agent_performance_reason}>
          <div className="flex items-center justify-center w-full h-full">
            <div className="bg-gray-200 w-4/5 h-2 rounded-full">
              <div
                className="bg-indigo-500 h-full rounded-full"
                style={{ width: `${params.value * 100}%` }}
              ></div>
            </div>
          </div>
        </Tippy>
      ),
      width: 150
    },
    {
      headerName: 'Customer Experience',
      headerTooltip: "Customer Experience | How was customer experience throughout the call?",
      tooltipValueGetter: (params) => {
        // Custom tooltip logic for this specific column
        return `Custom tooltip for ${params.value}`;
      },
      field: 'customer_experience_score',
      cellRenderer: (params) => (
        <Tippy content={params.data.customer_experience_score_reason}>
          <div className="flex items-center justify-center w-full h-full">
            <div className="bg-gray-200 w-4/5 h-2 rounded-full">
              <div
                className="bg-green-500 h-full rounded-full"
                style={{ width: `${params.value * 100}%` }}
              ></div>
            </div>
          </div>
        </Tippy>
      ),
      width: 150
    },
    {
      headerName: 'Customer Satisfied?',
      headerTooltip: "Customer Satisfied? | Was the customer satisfied at the end of the call?",
      field: 'customer_final_satisfaction',
      cellRenderer: (params) => getCustomerSatisfaction(params.value, params.data.customer_final_satisfaction_reason),
      width: 120
    },
    {
      headerName: 'Summary',
      field: 'summary',
      cellRenderer: (params) => (
        <Tippy content={params.value}>
          <button className="underline text-indigo-600 hover:text-indigo-800">
            View Summary
          </button>
        </Tippy>
      ),
      width: 120
    },
    {
      headerName: 'Major Intents',
      field: 'intents',
      flex: 1,
      cellRenderer: (params) => {
        if (!params.value) return null;
        return params.value.map((intent, index) => (
          <Tippy
            key={index}
            content={
              <div>
                <div><span className="text-xs text-gray-400">Category: </span>{intent.category}</div>
                <div><span className="text-xs text-gray-400">Major Intent: </span>{intent.standard_intent || intent.original_intent}</div>
                <div><span className="text-xs text-gray-400">Minor Intent: </span>{intent.original_intent}</div>
              </div>
            }
          >
            <span className="bg-indigo-100 text-indigo-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded cursor-pointer">
            {intent.standard_intent || intent.original_intent}
            </span>
          </Tippy>
        ));
      }
    },
  ];

  const pageSize = 20;

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalPageNumbers = 9; // Total number of page elements to show

    if (totalPages <= totalPageNumbers) {
      // If total pages are less than or equal to totalPageNumbers, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(
          <PageButton
            key={i}
            page={i}
            currentPage={currentPage}
            onClick={() => onPageChange(i)}
          />
        );
      }
    } else {
      // Always show first page
      pageNumbers.push(
        <PageButton
          key={1}
          page={1}
          currentPage={currentPage}
          onClick={() => onPageChange(1)}
        />
      );

      // Calculate start and end of middle section
      let startPage = Math.max(2, currentPage - 2);
      let endPage = Math.min(totalPages - 1, currentPage + 2);

      // Adjust if at the start or end
      if (currentPage <= 3) {
        endPage = Math.min(totalPages - 1, 5);
      }
      if (endPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 4);
      }

      // Add ellipsis if needed at the start
      if (startPage > 2) {
        pageNumbers.push(<EllipsisButton key="ellipsis-start" />);
      }

      //console.log(pageNumbers);

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
          <PageButton
            key={i}
            page={i}
            currentPage={currentPage}
            onClick={() => onPageChange(i)}
          />
        );
      }

      // Add ellipsis if needed at the end
      if (endPage < totalPages - 1) {
        pageNumbers.push(<EllipsisButton key="ellipsis-end" />);
      }

      // Always show last page
      pageNumbers.push(
        <PageButton
          key={totalPages}
          page={totalPages}
          currentPage={currentPage}
          onClick={() => onPageChange(totalPages)}
        />
      );
    }

    return pageNumbers;
  };

  const defaultColDef = useMemo(() => ({
    sortable: true,
    // ... other default column properties
  }), []);


  const onGridReady = useCallback((params) => {
    console.log('The grid is now ready', params);
    gridApiRef.current = params.api;
    if (sortModel && sortModel.length > 0) {
      params.api.applyColumnState({
        state: sortModel.map(model => ({
          colId: model.colId,
          sort: model.sort
        })),
        defaultState: { sort: null }
      });
    }
  }, [sortModel]);

  const onSortChanged = useCallback((event) => {
    if (isUpdatingRef.current) return;

    const newSortModel = event.api.getColumnState()
      .filter(column => column.sort)
      .map(column => ({
        colId: column.colId,
        sort: column.sort
      }));

    if (JSON.stringify(newSortModel) !== JSON.stringify(currentSortModelRef.current)) {
      currentSortModelRef.current = newSortModel;
      onSortChange(newSortModel);
    }
  }, [onSortChange]);

  useEffect(() => {
    if (gridApiRef.current && JSON.stringify(sortModel) !== JSON.stringify(currentSortModelRef.current)) {
      isUpdatingRef.current = true;
      gridApiRef.current.applyColumnState({
        state: sortModel.map(model => ({
          colId: model.colId,
          sort: model.sort
        })),
        defaultState: { sort: null }
      });
      currentSortModelRef.current = sortModel;
      isUpdatingRef.current = false;
    }
  }, [sortModel]);

  return (
    <div className="ag-theme-alpine" style={{ width: '100%' }}>
      <AgGridReact
        rowData={data}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        defaultColDef={defaultColDef}
        enableBrowserTooltips={true}
        onSortChanged={onSortChanged}
        sortingOrder={['asc', 'desc']}
        serverSideSorting={true}
        onGridReady={onGridReady}
      />
      <div className="flex justify-between items-center mt-6 bg-white p-4 rounded-lg shadow">
        <div className="text-sm text-gray-600">
          Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalRecs)} of {totalRecs} records
        </div>
        <div className="flex items-center space-x-2">
          <button
            className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <FaChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center space-x-1">
            {renderPageNumbers()}
          </div>
          <button
            className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            <FaChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

const PageButton = ({ page, currentPage, onClick }) => (
  <button
    className={`px-3 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${page === currentPage
        ? 'bg-indigo-600 text-white'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    onClick={onClick}
  >
    {page}
  </button>
);

const EllipsisButton = () => (
  <button className="px-3 py-1" disabled>
    <FaEllipsisH className="w-5 h-5 text-gray-400" />
  </button>
);

export default MainGrid;